import { useState, useEffect } from "react";
import axios from "axios";
import { Typography, Box, Grid, CircularProgress } from "@mui/material";
import { useAuth } from "../../contexts/AuthContext";
import NonSharedDiveCard from "./cards/NonSharedDiveCard";
import NonSharedDiveDetailsDialog from "./dialogs/NonSharedDiveDetailsDialog";

/**
 * Component for displaying a user's non-shared dives
 *
 * @param {Object} props
 * @param {string} props.userId - ID of the user whose dives to display
 */
function UserNonSharedDives({ userId }) {
  const [nonSharedDives, setNonSharedDives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedDive, setSelectedDive] = useState(null);

  const { userObjectId, currentUser } = useAuth();

  console.log("userObjectId:", userObjectId);

  useEffect(() => {
    const fetchNonSharedDives = async () => {
      // userObjectId is used in the URL, ensure it's in the dependency array.
      if (currentUser && userObjectId) {
        const currentTimestamp = new Date().toISOString();
        console.log(
          `[${currentTimestamp}] Frontend: UserNonSharedDives useEffect - Firing. userObjectId: ${userObjectId}`
        );
        setLoading(true);
        setError(null); // Reset error before new fetch

        try {
          setLoading(true);

          const token = await currentUser.getIdToken();
          console.log(
            `[${currentTimestamp}] Frontend: Making GET request to /api/dives/${userObjectId}/non-shared`
          );

          const response = await axios.get(
            `/api/dives/${userObjectId}/non-shared`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          console.log(
            `[${currentTimestamp}] Frontend: Response Status: ${response.status}`
          );
          console.log(
            `[${currentTimestamp}] Frontend: Response Headers:`,
            JSON.stringify(response.headers, null, 2)
          );
          console.log(
            `[${currentTimestamp}] Frontend: Full API Response Data:`,
            JSON.stringify(response.data, null, 2) // Log the full structure
          );

          if (response.status === 304) {
            console.warn(
              `[${currentTimestamp}] Frontend: Received 304 Not Modified. Browser will use its cached version.`
            );
            // For 304, response.data is empty. State remains as is.
          } else if (response.status >= 200 && response.status < 300) {
            // Check the structure of response.data
            if (response.data && Array.isArray(response.data.dives)) {
              // Handles paginated response like { dives: [...], totalPages: X }
              console.log(
                `[${currentTimestamp}] Frontend: Setting nonSharedDives from response.data.dives (paginated structure)`
              );
              setNonSharedDives(response.data.dives);
              // Example: You might also want to set pagination state here
              // if (typeof response.data.totalPages === 'number') {
              //   setTotalPages(response.data.totalPages);
              // }
              // if (typeof response.data.currentPage === 'number') {
              //   setCurrentPage(response.data.currentPage);
              // }
            } else if (Array.isArray(response.data)) {
              // Handles direct array response
              console.log(
                `[${currentTimestamp}] Frontend: Setting nonSharedDives from response.data (direct array structure)`
              );
              setNonSharedDives(response.data);
            } else {
              console.error(
                `[${currentTimestamp}] Frontend: API response data is not in a recognized format (expected array or object with 'dives' array). Received:`,
                response.data
              );
              setNonSharedDives([]); // Default to empty array to prevent .map error
              setError(
                "Received unexpected data format from server for non-shared dives."
              );
            }
          } else {
            // This block is less likely to be hit with Axios default behavior,
            // as non-2xx/304 statuses usually throw an error that's caught by the catch block.
            console.error(
              `[${currentTimestamp}] Frontend: Received unexpected status: ${response.status}`
            );
            throw new Error(`Request failed with status ${response.status}`);
          }
        } catch (err) {
          const errorTimestamp = new Date().toISOString();
          console.error(
            `[${errorTimestamp}] Frontend: Error fetching non-shared dives:`,
            err.message
          );
          if (err.response) {
            console.error(
              `[${errorTimestamp}] Frontend: Axios error response status: ${err.response.status}`
            );
            console.error(
              `[${errorTimestamp}] Frontend: Axios error response data:`,
              JSON.stringify(err.response.data, null, 2)
            );
          } else if (err.request) {
            console.error(
              `[${errorTimestamp}] Frontend: Axios error - No response received:`,
              err.request
            );
          } else {
            console.error(
              `[${errorTimestamp}] Frontend: Axios error - Request setup issue:`,
              err.message
            );
          }
          setError(err.message || "Failed to load dives");
          setNonSharedDives([]); // Ensure it's an array on error
        } finally {
          setLoading(false);
        }
      } else {
        console.log(
          `[${new Date().toISOString()}] Frontend: UserNonSharedDives - Skipped fetch because currentUser or userObjectId is missing.`
        );
        setLoading(false); // Don't stay in loading state
        setNonSharedDives([]); // Clear any existing dives to ensure it's an array
      }
    };

    fetchNonSharedDives();
    // IMPORTANT: Add dependencies to the useEffect array
  }, [
    currentUser,
    userObjectId /* any other state setters like setTotalPages if used inside */,
  ]);

  const handleViewDiveDetails = (dive) => {
    // Pass the full dive object instead of just the ID
    setSelectedDive(dive);
    setDetailsDialogOpen(true);
    console.log(`View details for dive ${dive._id}`);
  };

  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box py={3}>
        <Typography color="error">Error loading dives: {error}</Typography>
      </Box>
    );
  }

  if (nonSharedDives.length === 0) {
    return (
      <Box py={4}>
        <Typography variant="body1" color="textSecondary" align="center">
          You don't have any private dives yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box py={2}>
      <Grid container spacing={3}>
        {nonSharedDives.map((dive) => (
          <Grid item xs={12} sm={6} md={4} key={dive._id}>
            <NonSharedDiveCard
              dive={dive}
              onViewDetails={() => handleViewDiveDetails(dive)}
            />
          </Grid>
        ))}
      </Grid>
      <NonSharedDiveDetailsDialog
        open={detailsDialogOpen}
        onClose={handleCloseDetailsDialog}
        dive={selectedDive}
      />
    </Box>
  );
}

export default UserNonSharedDives;
